import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { returnItemDetails, getUserFromUidx, getOrdersStatus } from "../../../config/Services";
import { convertToLocalDateTime } from "../product/all-products/Middleware/commonUtils";
import usePrint from "../product/hooks/usePrint";
import "./ReturnsDetails.css";

/* ─── tiny SVG helpers ─────────────────────────────────────────────────────── */
const ArrowLeftSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </svg>
);

const ReceiptSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16 }}
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
    <path d="M14 8H8" />
    <path d="M16 12H8" />
    <path d="M13 16H8" />
  </svg>
);

const ShieldCheckSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16 }}
  >
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const iconForEvent = (event) => {
  const s = { width: 14, height: 14 };
  switch (event) {
    case "order_created":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={s}
        >
          <path d="M12 6v6h4" />
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
    case "payment":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={s}
        >
          <rect width="20" height="14" x="2" y="5" rx="2" />
          <line x1="2" x2="22" y1="10" y2="10" />
        </svg>
      );
    case "invoice":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={s}
        >
          <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
          <path d="M14 8H8" />
          <path d="M16 12H8" />
          <path d="M13 16H8" />
        </svg>
      );
    case "delivered":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={s}
        >
          <circle cx="12" cy="12" r="10" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "return":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={s}
        >
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </svg>
      );
    case "refund":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={s}
        >
          <rect width="20" height="12" x="2" y="6" rx="2" />
          <circle cx="12" cy="12" r="2" />
          <path d="M6 12h.01M18 12h.01" />
        </svg>
      );
    default:
      return null;
  }
};

/* ─── Status config map ──────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  CREATED: { label: "Order Created", icon: "order_created" },
  PARTIAL_PAYMENT_COMPLETED: { label: "Partial Payment", icon: "payment" },
  PAYMENT_COMPLETED: { label: "Payment Success", icon: "payment" },
  INVENTORY_CONSUMED: { label: "Tax Invoice Generated", icon: "invoice" },
  DELIVERED: { label: "Delivered", icon: "delivered" },
  RETURN_COMPLETED: { label: "Return Requested", icon: "return" },
  INVENTORY_RESTOCKED: { label: "Refund Processed", icon: "refund" },
};

/* ─────────────────────────────────────────────────────────────────────────── */

const ReturnsDetails = ({ returnId, orderId }) => {
  const navigate = useNavigate();
  const pathParams = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const resolvedReturnId = returnId || location?.state?.returnId;
  const resolvedOrderId = orderId || location?.state?.orderId;

  const { startPrint, isPrinting } = usePrint();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderData, setOrderData] = useState({});
  const [baseDetails, setBaseDetails] = useState({});
  const [orderDetails, setOrderDetails] = useState({});
  const [returnDetails, setReturnDetails] = useState({});
  const [date, setDate] = useState("");
  const [loggedUser, setLoggedUser] = useState("");
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [orderTimeLine, setOrderTimeLine] = useState([]);

  const RETURN_ID = searchParams.get("returnid");
  const ORDER_ID = searchParams.get("orderid");

  /* ── fetchData ── */
  const fetchData = async (response) => {
    const data = response?.data?.data;

    setOrderData(response?.data);
    setBaseDetails(data?.baseReturnResponse || {});
    setOrderDetails(data?.returnBillingDetails || {});
    setReturnDetails(data?.returnDetails || {});
    setDate(convertToLocalDateTime(data?.baseReturnResponse?.createdAt));

    setTotalQuantity(
      (data?.baseReturnResponse?.returnedItems || [])
        ?.map((item) => item?.quantity || item?.quantityBySpecs || 0)
        ?.reduce((a, b) => a + b, 0)
    );

    const userId = data?.baseReturnResponse?.loggedInUserId;
    if (userId) {
      try {
        const userResponse = await getUserFromUidx(userId);
        setLoggedUser(
          userResponse?.data?.data?.firstName + " " + userResponse?.data?.data?.secondName
        );
      } catch (_) {}
    }
  };

  /* ── fetch on mount ── */
  useEffect(() => {
    setLoading(true);
    const fetchDetails = async () => {
      try {
        const [response, orderTimelineResponse] = await Promise.all([
          returnItemDetails(RETURN_ID, ORDER_ID),
          getOrdersStatus(ORDER_ID),
        ]);

        await fetchData(response);

        const rawTimeline = orderTimelineResponse?.data?.data?.timeLine || [];
        const mapped = rawTimeline?.map((item) => {
          const config = STATUS_CONFIG[item?.fulfilmentStatus] || {
            label: item?.fulfilmentStatus,
            icon: "order_created",
          };
          return {
            key: item?.fulfilmentStatus,
            label: config?.label,
            actor: item?.updatedBy || "NA",
            time: item?.updateAt ? convertToLocalDateTime(item?.updateAt) : "—",
            icon: config?.icon,
          };
        });

        setOrderTimeLine(mapped);
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Failed to fetch return details");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [RETURN_ID, ORDER_ID]);

  /* ── derived display values ── */
  const returnedItems = baseDetails?.returnedItems || returnDetails?.returnedItems || [];
  const refund = baseDetails?.refund || "0";

  const totalReturnAmount = returnedItems?.reduce(
    (sum, item) =>
      sum +
      Number(item?.quantityBySpecs || item?.quantity || 0) *
        Number(item?.sellingPrice || item?.rate || 0),
    0
  );

  const refundAmount =
    returnDetails?.refundAmount || orderDetails?.returnAmount || totalReturnAmount;
  const paymentMode = orderDetails?.returnMode || orderDetails?.paymentMethod || "BANK TRANSFER";
  const refundStatus =
    orderDetails?.refunds?.[0]?.status === "COMPLETED"
      ? "Refund complete"
      : orderDetails?.returnStatus || "Refund complete";

  const customerName = baseDetails?.customerName || "—";
  const customerPhone = baseDetails?.mobileNumber || "—";
  const returnId_val = baseDetails?.returnId || resolvedReturnId || "—";
  const orderId_val = baseDetails?.orderId || resolvedOrderId || "—";
  const status = baseDetails?.orderStatus || "Returned";
  const dateTime = date || "—";

  // forcePick opens the browser's printer picker (navigator.usb.requestDevice) instead of silently
  // reusing the saved device. Callers must invoke this as handleReprint() — passing the function
  // reference straight to onClick would hand the click event in as forcePick, which is truthy.
  const handleReprint = (forcePick = true) => {
    startPrint({
      orderId: resolvedOrderId || ORDER_ID,
      returnId: resolvedReturnId || RETURN_ID,
      reprint: true,
      printType: "credit-note",
      skipKot: true,
      forcePick,
    });
  };

  /* ── render ── */
  return (
    <Navbar>
      <div className="returns-v2-page">
        {/* ── Top bar ── */}
        <div className="returns-v2-topbar">
          <div className="returns-v2-topbar-inner">
            <div className="returns-v2-topbar-left">
              <button
                className="returns-v2-back-btn"
                onClick={() => navigate("/orders")}
                aria-label="Go back"
              >
                <ArrowLeftSvg />
              </button>
              <div>
                <div className="returns-v2-title-row">
                  <h1 className="returns-v2-title">Return Summary</h1>
                  <span className="returns-v2-status-badge returned">
                    {baseDetails?.returnStatus || "Returned"}
                  </span>
                </div>
              </div>
            </div>
            <button
              className="returns-v2-reprint-btn"
              onClick={() => handleReprint()}
              disabled={isPrinting}
            >
              <ReceiptSvg />
              {isPrinting ? "Printing…" : "Reprint"}
            </button>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="returns-v2-content">
          {loading && (
            <div className="rv2-loading">
              <div className="rv2-spinner" />
              Loading return details…
            </div>
          )}

          {error && !loading && <div className="rv2-error">{error}</div>}

          {!loading && !error && (
            <>
              {/* ── Meta header card ── */}
              <div className="rv2-card">
                <div className="rv2-meta-grid">
                  <div className="rv2-meta-cell">
                    <p className="rv2-meta-label">Return ID</p>
                    <p className="rv2-meta-value">{returnId_val}</p>
                  </div>
                  <div className="rv2-meta-cell">
                    <p className="rv2-meta-label">Order ID</p>
                    <p className="rv2-meta-value">{orderId_val}</p>
                  </div>
                  <div className="rv2-meta-cell">
                    <p className="rv2-meta-label">Status</p>
                    <p className="rv2-meta-value">{status}</p>
                  </div>
                  <div className="rv2-meta-cell">
                    <p className="rv2-meta-label">Date / Time</p>
                    <p className="rv2-meta-value">{dateTime}</p>
                  </div>
                </div>
              </div>

              {/* ── Two-column body ── */}
              <div className="rv2-body-grid">
                {/* ── Timeline ── */}
                <div className="rv2-timeline-card">
                  <div className="rv2-timeline-header">
                    <div className="rv2-timeline-icon-wrap">
                      <ShieldCheckSvg />
                    </div>
                    <div className="rv2-timeline-header-text">
                      <p>Timeline</p>
                      <h2>Order + Return lifecycle</h2>
                    </div>
                  </div>

                  <div className="rv2-timeline-items">
                    {orderTimeLine.length === 0 ? (
                      <div style={{ color: "#94a3b8", fontSize: 13, padding: "8px 0" }}>
                        No timeline data
                      </div>
                    ) : (
                      orderTimeLine?.map((ev, idx) => (
                        <div key={`${ev?.key}-${idx}`} className="rv2-timeline-item">
                          <div className="rv2-timeline-dot">{iconForEvent(ev?.icon)}</div>
                          <p className="rv2-timeline-event-name">{ev?.label}</p>
                          <p className="rv2-timeline-actor">by {ev?.actor}</p>
                          <p className="rv2-timeline-time">{ev?.time}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* ── Right panel ── */}
                <div className="rv2-right-panel">
                  {/* ── Info cards row ── */}
                  <div className="rv2-info-cards-row">
                    {/* Customer Info */}
                    <div className="rv2-info-card">
                      <p className="rv2-info-card-label">Customer Info</p>
                      <div className="rv2-info-fields">
                        <div>
                          <p className="rv2-field-label">Name</p>
                          <p className="rv2-field-value">{customerName}</p>
                        </div>
                        <div>
                          <p className="rv2-field-label">Phone</p>
                          <p className="rv2-field-value">{customerPhone}</p>
                        </div>
                        {loggedUser && (
                          <div>
                            <p className="rv2-field-label">Cashier</p>
                            <p className="rv2-field-value">{loggedUser}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Return Summary */}
                    <div className="rv2-info-card">
                      <p className="rv2-info-card-label">Return Summary</p>
                      <div className="rv2-summary-rows">
                        <div className="rv2-summary-row">
                          <span className="rv2-summary-row-key">Total Return Amount</span>
                          <span className="rv2-summary-row-val">
                            ₹{Number(refund).toLocaleString("en-IN")}
                          </span>
                        </div>
                        <div className="rv2-summary-row">
                          <span className="rv2-summary-row-key">Payment Mode</span>
                          <span className="rv2-summary-row-val">{paymentMode}</span>
                        </div>
                        <div className="rv2-summary-row">
                          <span className="rv2-summary-row-key">Refund Status</span>
                          <span className="rv2-summary-row-val">{refundStatus}</span>
                        </div>
                      </div>
                    </div>

                    {/* Refund Info */}
                    <div className="rv2-info-card">
                      <p className="rv2-info-card-label">Refund Info</p>
                      <div className="rv2-refund-cells">
                        <div className="rv2-refund-cell">
                          <p className="rv2-field-label">Refund Amount</p>
                          <p className="rv2-field-value">
                            ₹{Number(refundAmount).toLocaleString("en-IN")}
                          </p>
                        </div>
                        <div className="rv2-refund-cell">
                          <p className="rv2-field-label">Mode</p>
                          <p className="rv2-field-value">{paymentMode}</p>
                        </div>
                        <div className="rv2-refund-cell">
                          <p className="rv2-field-label">Processed By</p>
                          <p className="rv2-field-value">
                            {baseDetails?.createdBy || baseDetails?.createdByName || "Accounts"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Returned Items Grid ── */}
                  <div className="rv2-table-card">
                    <div className="rv2-table-card-header">
                      <p>Returned Items</p>
                    </div>
                    <div className="rv2-grid-table">
                      <div className="rv2-grid-head">
                        <span>Item Name</span>
                        <span>Returned Qty</span>
                        <span>Rate</span>
                        <span className="text-right">Amount</span>
                      </div>
                      {returnedItems?.length === 0 ? (
                        <div className="rv2-grid-empty">No returned items found</div>
                      ) : (
                        returnedItems?.map((item, idx) => {
                          const qty = Number(
                            item?.returnedQuantity || item?.quantityBySpecs || item?.quantity || 0
                          );
                          const rate = Number(
                            item?.returnedSubTotal || item?.sellingPrice || item?.rate || 0
                          );
                          const amount = qty * rate;
                          return (
                            <div key={item?.productId || idx} className="rv2-grid-row">
                              <span className="rv2-grid-item-name">
                                {item?.productName || item?.name || "—"}
                              </span>
                              <span className="rv2-grid-cell">{qty}</span>
                              <span className="rv2-grid-cell">
                                ₹{item?.sellingPrice?.toLocaleString("en-IN")}
                              </span>
                              <span className="rv2-grid-cell rv2-grid-cell--right">
                                ₹{rate?.toLocaleString("en-IN")}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
                {/* /rv2-right-panel */}
              </div>
              {/* /rv2-body-grid */}
            </>
          )}
        </div>
      </div>
    </Navbar>
  );
};

export default ReturnsDetails;
